import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { SpiceIndicator } from "@/components/SpiceIndicator";
import { useCart } from "@/context/CartContext";
import { money } from "@/config";

const SIZE_ORDER = ["100ml", "250ml", "500ml", "1000ml"];

export function ProductCard({ product, index = 0 }) {
  const { addItem, setOpen } = useCart();
  const isKit = product.category === "kit";
  const sizes = SIZE_ORDER.filter((s) => product.variants.some((v) => v.id === s));
  const [sizeId, setSizeId] = useState(sizes.includes("250ml") ? "250ml" : product.variants[0]?.id);
  const variant = product.variants.find((v) => v.id === sizeId) || product.variants[0];
  const outOfStock = product.variants.every((v) => v.stock <= 0);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!variant || variant.stock <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    addItem(product, variant, 1, rect);
    toast.success(`${product.name} (${variant.label}) agregado`, {
      action: { label: "Ver carrito", onClick: () => setOpen(true) },
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      whileHover={{ rotateY: -6, rotateX: 3, scale: 1.02 }}
      style={{ transformPerspective: 900 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-sand bg-white shadow-[0_8px_30px_rgba(224,30,38,0.08)]"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/producto/${product.slug}`} className="block" data-testid={`product-link-${product.slug}`}>
        <div className="relative flex h-64 items-center justify-center overflow-hidden bg-surface p-6">
          <img
            src={product.image}
            alt={`Frasco de ${product.name} — SoyDiVa`}
            loading="lazy"
            className="h-full w-auto object-contain jar-shadow transition-[filter,transform] duration-500 group-hover:jar-shadow-hover group-hover:scale-105"
          />
          {isKit ? (
            <span className="absolute left-3 top-3 rounded-full bg-cocoa px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-cream">
              Kit especial
            </span>
          ) : (
            product.featured && (
              <span className="absolute left-3 top-3 rounded-full bg-corn px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-cocoa">
                Consentida
              </span>
            )
          )}
          {outOfStock && (
            <span className="absolute right-3 top-3 rounded-full bg-cocoa px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              Agotado
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-tight text-cocoa">
            <Link to={`/producto/${product.slug}`} className="transition-colors hover:text-habanero">
              {product.name}
            </Link>
          </h3>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={variant?.id}
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="whitespace-nowrap font-display text-lg font-bold text-habanero"
              data-testid={`product-price-${product.slug}`}
            >
              {variant ? money(variant.price) : ""}
            </motion.span>
          </AnimatePresence>
        </div>
        <p className="line-clamp-2 text-sm text-clay">{product.short}</p>
        {!isKit && sizes.length > 1 && (
          <div className="mt-1 flex flex-wrap gap-1.5" role="group" aria-label="Elige el tamaño">
            {sizes.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`size-chip-${product.slug}-${s}`}
                onClick={() => setSizeId(s)}
                aria-pressed={sizeId === s}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                  sizeId === s
                    ? "border-habanero bg-habanero text-white"
                    : "border-sand bg-white text-clay hover:border-habanero hover:text-habanero"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between pt-2">
          {isKit ? (
            <span className="text-xs font-semibold text-clay">5 mini frascos</span>
          ) : (
            <SpiceIndicator level={product.spice_level} interactive showLabel={false} />
          )}
          <button
            data-testid={`product-add-${product.slug}`}
            disabled={outOfStock || !variant || variant.stock <= 0}
            onClick={handleAdd}
            aria-label={`Agregar ${product.name} al carrito`}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-habanero text-white transition-colors hover:bg-habanero-dark disabled:cursor-not-allowed disabled:bg-sand"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}
